import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Settings, Palette, Building2, User, Shield, Database,
  Save, AlertCircle, CheckCircle, Eye, EyeOff, Target,
  Plus, Edit, Trash2, Users, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TenantSettings = ({ getAuthHeaders }) => {
  const [activeTab, setActiveTab] = useState('branding');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Branding state (moved from BrandingPanel)
  const [brandingData, setBrandingData] = useState({
    company_name: '',
    logo_url: '',
    brand_colors: {
      primary: '#3B82F6',
      secondary: '#1F2937',
      accent: '#10B981'
    }
  });

  // Company profile state
  const [companyProfile, setCompanyProfile] = useState({
    company_name: '',
    address_line1: '',
    address_line2: '',
    address_line3: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    phone: '',
    email: '',
    website: '',
    registration_number: '',
    vat_number: '',
    business_hours: '',
    timezone: 'Europe/London',
    description: ''
  });

  // User preferences state
  const [userPreferences, setUserPreferences] = useState({
    notifications: {
      email_notifications: true,
      sms_notifications: false,
      push_notifications: true
    },
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    language: 'en'
  });

  // Security state
  const [securityData, setSecurityData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_factor_enabled: false
  });

  // System preferences state
  const [systemPreferences, setSystemPreferences] = useState({
    auto_backup: true,
    data_retention_days: 365,
    export_format: 'excel'
  });

  // Bank details state
  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    account_name: '',
    sort_code: '',
    account_number: ''
  });

  // Lead sources state
  const [leadSources, setLeadSources] = useState([]);
  const [suggestedSources, setSuggestedSources] = useState([]);
  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceFormData, setSourceFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchBrandingData(),
        fetchCompanyProfile(),
        fetchUserPreferences(),
        fetchSystemPreferences(),
        fetchBankDetails(),
        fetchLeadSources(),
        fetchSuggestedSources()
      ]);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBrandingData = async () => {
    try {
      const response = await axios.get(`${API}/branding/my`, getAuthHeaders());
      setBrandingData(response.data);
    } catch (error) {
      console.error('Failed to fetch branding data:', error);
    }
  };

  const fetchCompanyProfile = async () => {
    try {
      // This endpoint might need to be created or modified
      const response = await axios.get(`${API}/company/profile`, getAuthHeaders());
      setCompanyProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch company profile:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      // This endpoint might need to be created
      const response = await axios.get(`${API}/user/preferences`, getAuthHeaders());
      setUserPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
    }
  };

  const fetchSystemPreferences = async () => {
    try {
      const response = await axios.get(`${API}/system/preferences`, getAuthHeaders());
      setSystemPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch system preferences:', error);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const response = await axios.get(`${API}/company/bank-details`, getAuthHeaders());
      setBankDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch bank details:', error);
    }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await axios.get(`${API}/lead-sources`, getAuthHeaders());
      setLeadSources(response.data);
    } catch (error) {
      console.error('Failed to fetch lead sources:', error);
    }
  };

  const fetchSuggestedSources = async () => {
    try {
      const response = await axios.get(`${API}/lead-sources/suggestions`, getAuthHeaders());
      setSuggestedSources(response.data);
    } catch (error) {
      console.error('Failed to fetch suggested sources:', error);
    }
  };

  const saveBranding = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/branding/my`, brandingData, getAuthHeaders());
      toast.success('Branding settings saved successfully');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setIsSaving(false);
    }
  };

  const saveCompanyProfile = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/company/profile`, companyProfile, getAuthHeaders());
      toast.success('Company profile saved successfully');
    } catch (error) {
      toast.error('Failed to save company profile');
    } finally {
      setIsSaving(false);
    }
  };

  const saveUserPreferences = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/user/preferences`, userPreferences, getAuthHeaders());
      toast.success('User preferences saved successfully');
    } catch (error) {
      toast.error('Failed to save user preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const saveSystemPreferences = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/system/preferences`, systemPreferences, getAuthHeaders());
      toast.success('System preferences saved successfully');
    } catch (error) {
      toast.error('Failed to save system preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const saveBankDetails = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/company/bank-details`, bankDetails, getAuthHeaders());
      toast.success('Bank details saved successfully');
    } catch (error) {
      toast.error('Failed to save bank details');
    } finally {
      setIsSaving(false);
    }
  };

  const changePassword = async () => {
    if (securityData.new_password !== securityData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (securityData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: securityData.current_password,
        new_password: securityData.new_password
      }, getAuthHeaders());
      
      toast.success('Password changed successfully');
      setSecurityData({
        current_password: '',
        new_password: '',
        confirm_password: '',
        two_factor_enabled: securityData.two_factor_enabled
      });
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const saveLeadSource = async () => {
    setIsSaving(true);
    try {
      if (editingSource) {
        await axios.put(`${API}/lead-sources/${editingSource.id}`, sourceFormData, getAuthHeaders());
        toast.success('Lead source updated successfully');
      } else {
        await axios.post(`${API}/lead-sources`, sourceFormData, getAuthHeaders());
        toast.success('Lead source created successfully');
      }
      setIsSourceDialogOpen(false);
      setEditingSource(null);
      setSourceFormData({ name: '', description: '', is_active: true });
      fetchLeadSources();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to save lead source';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteLeadSource = async (sourceId, sourceName) => {
    if (window.confirm(`Are you sure you want to delete "${sourceName}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/lead-sources/${sourceId}`, getAuthHeaders());
        toast.success('Lead source deleted successfully');
        fetchLeadSources();
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Failed to delete lead source';
        toast.error(errorMessage);
      }
    }
  };

  const openSourceDialog = (source = null) => {
    if (source) {
      setEditingSource(source);
      setSourceFormData({
        name: source.name,
        description: source.description || '',
        is_active: source.is_active
      });
    } else {
      setEditingSource(null);
      setSourceFormData({ name: '', description: '', is_active: true });
    }
    setIsSourceDialogOpen(true);
  };

  const addSuggestedSource = async (suggested) => {
    try {
      await axios.post(`${API}/lead-sources`, {
        name: suggested.name,
        description: suggested.description,
        is_active: true
      }, getAuthHeaders());
      toast.success(`Added "${suggested.name}" to your lead sources`);
      fetchLeadSources();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to add lead source';
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Settings className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="w-8 h-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
            Settings
          </h1>
          <p className="text-gray-500">Manage your account, company, and system preferences</p>
        </div>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="company" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Company
          </TabsTrigger>
          <TabsTrigger value="bank" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Bank Details
          </TabsTrigger>
          <TabsTrigger value="sources" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Lead Sources
          </TabsTrigger>
          <TabsTrigger value="user" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Preferences
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            System
          </TabsTrigger>
        </TabsList>

        {/* Branding & Appearance */}
        <TabsContent value="branding">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Branding & Appearance</h3>
                <p className="text-gray-600 mb-6">Customize your company's visual identity and branding</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    value={brandingData.company_name}
                    onChange={(e) => setBrandingData({ ...brandingData, company_name: e.target.value })}
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <Label htmlFor="logo-url">Logo URL</Label>
                  <Input
                    id="logo-url"
                    value={brandingData.logo_url}
                    onChange={(e) => setBrandingData({ ...brandingData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-4 block">Brand Colors</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="primary-color" className="text-sm">Primary Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="primary-color"
                        value={brandingData.brand_colors.primary}
                        onChange={(e) => setBrandingData({
                          ...brandingData,
                          brand_colors: { ...brandingData.brand_colors, primary: e.target.value }
                        })}
                        className="w-12 h-10 rounded border"
                      />
                      <Input
                        value={brandingData.brand_colors.primary}
                        onChange={(e) => setBrandingData({
                          ...brandingData,
                          brand_colors: { ...brandingData.brand_colors, primary: e.target.value }
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="secondary-color" className="text-sm">Secondary Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="secondary-color"
                        value={brandingData.brand_colors.secondary}
                        onChange={(e) => setBrandingData({
                          ...brandingData,
                          brand_colors: { ...brandingData.brand_colors, secondary: e.target.value }
                        })}
                        className="w-12 h-10 rounded border"
                      />
                      <Input
                        value={brandingData.brand_colors.secondary}
                        onChange={(e) => setBrandingData({
                          ...brandingData,
                          brand_colors: { ...brandingData.brand_colors, secondary: e.target.value }
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="accent-color" className="text-sm">Accent Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="accent-color"
                        value={brandingData.brand_colors.accent}
                        onChange={(e) => setBrandingData({
                          ...brandingData,
                          brand_colors: { ...brandingData.brand_colors, accent: e.target.value }
                        })}
                        className="w-12 h-10 rounded border"
                      />
                      <Input
                        value={brandingData.brand_colors.accent}
                        onChange={(e) => setBrandingData({
                          ...brandingData,
                          brand_colors: { ...brandingData.brand_colors, accent: e.target.value }
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveBranding} disabled={isSaving} className="gap-2">
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save Branding'}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Company Profile */}
        <TabsContent value="company">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Company Profile</h3>
                <p className="text-gray-600 mb-6">Manage your company information and business details</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="comp-name">Company Name *</Label>
                  <Input
                    id="comp-name"
                    value={companyProfile.company_name}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, company_name: e.target.value })}
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="comp-email">Company Email *</Label>
                  <Input
                    id="comp-email"
                    type="email"
                    value={companyProfile.email}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, email: e.target.value })}
                    placeholder="contact@company.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="comp-phone">Phone Number</Label>
                  <Input
                    id="comp-phone"
                    value={companyProfile.phone}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, phone: e.target.value })}
                    placeholder="+44 20 1234 5678"
                  />
                </div>

                <div>
                  <Label htmlFor="comp-website">Website</Label>
                  <Input
                    id="comp-website"
                    value={companyProfile.website}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, website: e.target.value })}
                    placeholder="https://www.company.com"
                  />
                </div>

                <div>
                  <Label htmlFor="reg-number">Company Registration Number</Label>
                  <Input
                    id="reg-number"
                    value={companyProfile.registration_number}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, registration_number: e.target.value })}
                    placeholder="12345678"
                  />
                </div>

                <div>
                  <Label htmlFor="vat-number">VAT Number</Label>
                  <Input
                    id="vat-number"
                    value={companyProfile.vat_number}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, vat_number: e.target.value })}
                    placeholder="GB123456789"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-4 block">Business Address</Label>
                <div className="grid grid-cols-1 gap-4">
                  <Input
                    value={companyProfile.address_line1}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, address_line1: e.target.value })}
                    placeholder="Address Line 1"
                  />
                  <Input
                    value={companyProfile.address_line2}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, address_line2: e.target.value })}
                    placeholder="Address Line 2 (optional)"
                  />
                  <Input
                    value={companyProfile.address_line3}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, address_line3: e.target.value })}
                    placeholder="Address Line 3 (optional)"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      value={companyProfile.city}
                      onChange={(e) => setCompanyProfile({ ...companyProfile, city: e.target.value })}
                      placeholder="City"
                    />
                    <Input
                      value={companyProfile.county}
                      onChange={(e) => setCompanyProfile({ ...companyProfile, county: e.target.value })}
                      placeholder="County"
                    />
                    <Input
                      value={companyProfile.postcode}
                      onChange={(e) => setCompanyProfile({ ...companyProfile, postcode: e.target.value })}
                      placeholder="Postcode"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="business-hours">Business Hours</Label>
                  <Input
                    id="business-hours"
                    value={companyProfile.business_hours}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, business_hours: e.target.value })}
                    placeholder="Mon-Fri 9:00-17:00"
                  />
                </div>

                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <select
                    id="timezone"
                    value={companyProfile.timezone}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, timezone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Dublin">Europe/Dublin</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Company Description</Label>
                <textarea
                  id="description"
                  value={companyProfile.description}
                  onChange={(e) => setCompanyProfile({ ...companyProfile, description: e.target.value })}
                  className="w-full min-h-24 px-3 py-2 border rounded-md"
                  placeholder="Brief description of your company and services..."
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={saveCompanyProfile} disabled={isSaving} className="gap-2">
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save Company Profile'}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Bank Details */}
        <TabsContent value="bank">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
                <p className="text-gray-600 mb-6">Manage your banking information for quotes and invoices</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="bank-name">Bank Name</Label>
                  <Input
                    id="bank-name"
                    value={bankDetails.bank_name}
                    onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                    placeholder="e.g., Barclays Bank"
                  />
                </div>

                <div>
                  <Label htmlFor="account-name">Account Name</Label>
                  <Input
                    id="account-name"
                    value={bankDetails.account_name}
                    onChange={(e) => setBankDetails({ ...bankDetails, account_name: e.target.value })}
                    placeholder="e.g., Your Company Name Ltd"
                  />
                </div>

                <div>
                  <Label htmlFor="sort-code">Sort Code</Label>
                  <Input
                    id="sort-code"
                    value={bankDetails.sort_code}
                    onChange={(e) => setBankDetails({ ...bankDetails, sort_code: e.target.value })}
                    placeholder="e.g., 12-34-56"
                    maxLength="8"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: XX-XX-XX</p>
                </div>

                <div>
                  <Label htmlFor="account-number">Account Number</Label>
                  <Input
                    id="account-number"
                    value={bankDetails.account_number}
                    onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
                    placeholder="e.g., 12345678"
                    maxLength="8"
                  />
                  <p className="text-xs text-gray-500 mt-1">Usually 8 digits</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">Banking Information Usage</h4>
                    <p className="text-sm text-blue-700">
                      This information will be displayed on your quotes and invoices in the "Payment Information" section 
                      to help customers make payments to your business.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveBankDetails} disabled={isSaving} className="gap-2">
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save Bank Details'}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Lead Sources */}
        <TabsContent value="sources">
          <Card className="p-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Lead Sources Management</h3>
                  <p className="text-gray-600 mb-6">Manage where your customers come from to track marketing effectiveness</p>
                </div>
                <Button onClick={() => openSourceDialog()} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Source
                </Button>
              </div>

              {/* Current Lead Sources */}
              <div>
                <h4 className="font-medium mb-4">Your Lead Sources</h4>
                {leadSources.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No lead sources configured yet</p>
                    <Button onClick={() => openSourceDialog()} className="gap-2">
                      <Plus className="w-4 h-4" />
                      Add Your First Source
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {leadSources.map((source) => (
                      <Card key={source.id} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h5 className="font-medium text-sm">{source.name}</h5>
                            {source.description && (
                              <p className="text-xs text-gray-500 mt-1">{source.description}</p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSourceDialog(source)}
                              title="Edit Source"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteLeadSource(source.id, source.name)}
                              className="text-red-600"
                              title="Delete Source"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-600">{source.customer_count} customers</span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            source.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {source.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Suggested Sources */}
              {suggestedSources.length > 0 && (
                <div>
                  <h4 className="font-medium mb-4">Suggested Lead Sources</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Common sources used by car cleaning businesses. Click to add to your list.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {suggestedSources
                      .filter(suggested => !leadSources.some(source => source.name === suggested.name))
                      .map((suggested) => (
                        <Button
                          key={suggested.name}
                          variant="outline"
                          size="sm"
                          className="justify-start text-left h-auto py-2 px-3"
                          onClick={() => addSuggestedSource(suggested)}
                        >
                          <div>
                            <div className="font-medium text-xs">{suggested.name}</div>
                            <div className="text-xs text-gray-500 truncate">{suggested.description}</div>
                          </div>
                        </Button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* User Preferences */}
        <TabsContent value="user">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">User Preferences</h3>
                <p className="text-gray-600 mb-6">Customize your personal settings and preferences</p>
              </div>

              <div>
                <Label className="mb-4 block">Notifications</Label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="email-notifications"
                      checked={userPreferences.notifications.email_notifications}
                      onChange={(e) => setUserPreferences({
                        ...userPreferences,
                        notifications: { ...userPreferences.notifications, email_notifications: e.target.checked }
                      })}
                      className="rounded"
                    />
                    <Label htmlFor="email-notifications">Email notifications</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="sms-notifications"
                      checked={userPreferences.notifications.sms_notifications}
                      onChange={(e) => setUserPreferences({
                        ...userPreferences,
                        notifications: { ...userPreferences.notifications, sms_notifications: e.target.checked }
                      })}
                      className="rounded"
                    />
                    <Label htmlFor="sms-notifications">SMS notifications</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="push-notifications"
                      checked={userPreferences.notifications.push_notifications}
                      onChange={(e) => setUserPreferences({
                        ...userPreferences,
                        notifications: { ...userPreferences.notifications, push_notifications: e.target.checked }
                      })}
                      className="rounded"
                    />
                    <Label htmlFor="push-notifications">Push notifications</Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="date-format">Date Format</Label>
                  <select
                    id="date-format"
                    value={userPreferences.date_format}
                    onChange={(e) => setUserPreferences({ ...userPreferences, date_format: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="time-format">Time Format</Label>
                  <select
                    id="time-format"
                    value={userPreferences.time_format}
                    onChange={(e) => setUserPreferences({ ...userPreferences, time_format: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="24h">24 Hour</option>
                    <option value="12h">12 Hour</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="language">Language</Label>
                  <select
                    id="language"
                    value={userPreferences.language}
                    onChange={(e) => setUserPreferences({ ...userPreferences, language: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveUserPreferences} disabled={isSaving} className="gap-2">
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Account & Security */}
        <TabsContent value="security">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Account & Security</h3>
                <p className="text-gray-600 mb-6">Manage your account security settings</p>
              </div>

              <div>
                <h4 className="font-medium mb-4">Change Password</h4>
                <div className="space-y-4 max-w-md">
                  <div>
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? "text" : "password"}
                        value={securityData.current_password}
                        onChange={(e) => setSecurityData({ ...securityData, current_password: e.target.value })}
                        placeholder="Enter current password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        value={securityData.new_password}
                        onChange={(e) => setSecurityData({ ...securityData, new_password: e.target.value })}
                        placeholder="Enter new password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={securityData.confirm_password}
                      onChange={(e) => setSecurityData({ ...securityData, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <Button onClick={changePassword} disabled={isSaving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {isSaving ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Two-Factor Authentication</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="two-factor"
                    checked={securityData.two_factor_enabled}
                    onChange={(e) => setSecurityData({ ...securityData, two_factor_enabled: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="two-factor">Enable two-factor authentication</Label>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Add an extra layer of security to your account
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* System Preferences */}
        <TabsContent value="system">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">System Preferences</h3>
                <p className="text-gray-600 mb-6">Configure system settings and data management</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="auto-backup"
                    checked={systemPreferences.auto_backup}
                    onChange={(e) => setSystemPreferences({ ...systemPreferences, auto_backup: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="auto-backup">Enable automatic data backups</Label>
                </div>

                <div>
                  <Label htmlFor="retention-days">Data Retention (days)</Label>
                  <Input
                    id="retention-days"
                    type="number"
                    value={systemPreferences.data_retention_days}
                    onChange={(e) => setSystemPreferences({ ...systemPreferences, data_retention_days: parseInt(e.target.value) })}
                    className="max-w-xs"
                    min="30"
                    max="3650"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    How long to keep historical data (30-3650 days)
                  </p>
                </div>

                <div>
                  <Label htmlFor="export-format">Default Export Format</Label>
                  <select
                    id="export-format"
                    value={systemPreferences.export_format}
                    onChange={(e) => setSystemPreferences({ ...systemPreferences, export_format: e.target.value })}
                    className="w-full max-w-xs px-3 py-2 border rounded-md"
                  >
                    <option value="excel">Excel (.xlsx)</option>
                    <option value="csv">CSV (.csv)</option>
                    <option value="pdf">PDF (.pdf)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveSystemPreferences} disabled={isSaving} className="gap-2">
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving...' : 'Save System Preferences'}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Lead Source Dialog */}
      <Dialog open={isSourceDialogOpen} onOpenChange={setIsSourceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSource ? 'Edit Lead Source' : 'Add New Lead Source'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="source-name">Source Name *</Label>
              <Input
                id="source-name"
                value={sourceFormData.name}
                onChange={(e) => setSourceFormData({ ...sourceFormData, name: e.target.value })}
                placeholder="e.g., Google Ads, Word of Mouth"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="source-description">Description</Label>
              <Input
                id="source-description"
                value={sourceFormData.description}
                onChange={(e) => setSourceFormData({ ...sourceFormData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="source-active"
                checked={sourceFormData.is_active}
                onChange={(e) => setSourceFormData({ ...sourceFormData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="source-active">Active (available for new customers)</Label>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                onClick={saveLeadSource} 
                disabled={isSaving || !sourceFormData.name.trim()}
                className="flex-1"
              >
                {isSaving ? 'Saving...' : (editingSource ? 'Update Source' : 'Add Source')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsSourceDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantSettings;